import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';
import BusinessWallet from './BusinessWallet';

function BusinessDashboard() {
    const [campaigns, setCampaigns] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('campaigns');
    
    const [showCampaignForm, setShowCampaignForm] = useState(false);
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState('');

    const navigate = useNavigate();
    const location = useLocation();

    const [campaignData, setCampaignData] = useState({
        title: '', description: '', totalBudget: '', taskCount: '', campaignType: 'PRODUCT_LAUNCH', targetAudience: ''
    });

    const [taskData, setTaskData] = useState({
        title: '', description: '', reward: '', deadline: '', proofType: 'URL', instructions: '', assetUrl: '', minTier: 'NEW'
    });

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        const rewardValue = parseFloat(taskData.reward) || 0;
        
        if (rewardValue < 3 && taskData.minTier !== 'NEW') {
            setTaskData(prev => ({ ...prev, minTier: 'NEW' }));
        } else if (rewardValue < 5 && (taskData.minTier === 'GOLD' || taskData.minTier === 'PLATINUM')) {
            setTaskData(prev => ({ ...prev, minTier: 'TRUSTED' }));
        } else if (rewardValue < 10 && taskData.minTier === 'PLATINUM') {
            setTaskData(prev => ({ ...prev, minTier: 'GOLD' }));
        }
    }, [taskData.reward, taskData.minTier]);

    const isTierDisabled = (tier) => {
        const rewardValue = parseFloat(taskData.reward) || 0;
        if (tier === 'TRUSTED' && rewardValue < 3) return true;
        if (tier === 'GOLD' && rewardValue < 5) return true;
        if (tier === 'PLATINUM' && rewardValue < 10) return true;
        return false;
    };

    useEffect(() => {
        if (!token || role !== 'BUSINESS') {
            navigate('/');
            return;
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const campaignsRes = await axios.get(`${API_URL}/api/business/campaign-requests`, { headers: { Authorization: `Bearer ${token}` } });
            setCampaigns(campaignsRes.data.content || campaignsRes.data);

            const tasksRes = await axios.get(`${API_URL}/api/business/tasks`, { headers: { Authorization: `Bearer ${token}` } });
            setTasks(tasksRes.data.content || tasksRes.data);

            const subsRes = await axios.get(`${API_URL}/api/business/submissions`, { headers: { Authorization: `Bearer ${token}` } });
            setSubmissions(subsRes.data.content || subsRes.data);
        } catch (err) {
            toast.error('Failed to load data');
        }
        setLoading(false);
    };

    const handleCreateCampaign = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/business/campaign-requests`, campaignData, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Campaign created successfully!');
            setShowCampaignForm(false);
            setCampaignData({ title: '', description: '', totalBudget: '', taskCount: '', campaignType: 'PRODUCT_LAUNCH', targetAudience: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create campaign. Check your wallet balance.');
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/business/campaign-requests/${selectedCampaignId}/tasks`, taskData, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Task created successfully!');
            setShowTaskForm(false);
            setTaskData({ title: '', description: '', reward: '', deadline: '', proofType: 'URL', instructions: '', assetUrl: '', minTier: 'NEW' });
            fetchData();
        } catch (err) {
            toast.error('Failed to create task');
        }
    };

    const handleApproveSubmission = async (id) => {
        try {
            await axios.put(`${API_URL}/api/business/submissions/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Submission approved!');
            fetchData();
        } catch (err) {
            toast.error('Failed to approve submission');
        }
    };

    const handleRejectSubmission = async (id) => {
        try {
            await axios.put(`${API_URL}/api/business/submissions/${id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Submission rejected!');
            fetchData();
        } catch (err) {
            toast.error('Failed to reject submission');
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING': case 'PENDING_BUSINESS': return 'badge badge-warning';
            case 'APPROVED': case 'ACTIVE': case 'COMPLETED': return 'badge badge-success';
            case 'REJECTED': return 'badge badge-error';
            default: return 'badge';
        }
    };

    if (loading) {
        return (
            <div className="dashboard-layout">
                <aside className="sidebar">
                    <Link to="/" className="sidebar-brand">infloir</Link>
                    <nav className="sidebar-nav">
                        <div className="nav-item" style={{ opacity: 0.3 }}><span>🏢</span> Dashboard</div>
                        <div className="nav-item" style={{ opacity: 0.3 }}><span>💰</span> Spending</div>
                        <div className="nav-item" style={{ opacity: 0.3 }}><span>👤</span> Profile</div>
                    </nav>
                </aside>
                <div className="main-content">
                    <header className="topbar">
                        <div style={{ width: '150px', height: '20px', background: 'var(--bg-hover)', borderRadius: '4px' }}></div>
                    </header>
                    <main className="page-container">
                        {/* Skeleton for Wallet */}
                        <div className="card" style={{ height: '180px', background: 'var(--bg-hover)', animation: 'pulse 2s infinite', marginBottom: '32px' }}></div>
                        
                        {/* Skeleton for Stats */}
                        <div className="stats-grid" style={{ marginBottom: '32px' }}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="card" style={{ height: '100px', background: 'var(--bg-hover)', animation: 'pulse 2s infinite' }}></div>
                            ))}
                        </div>

                        {/* Skeleton for Tabs & Table */}
                        <div style={{ height: '40px', width: '400px', background: 'var(--bg-hover)', borderRadius: '4px', marginBottom: '24px', animation: 'pulse 2s infinite' }}></div>
                        <div className="card" style={{ height: '300px', background: 'var(--bg-hover)', animation: 'pulse 2s infinite' }}></div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-layout">
            <aside className="sidebar">
                <Link to="/" className="sidebar-brand">infloir</Link>
                <nav className="sidebar-nav">
                    <Link to="/business-dashboard" className={`nav-item ${location.pathname === '/business-dashboard' ? 'active' : ''}`}>
                        <span>🏢</span> Dashboard
                    </Link>
                    <Link to="/business/spending" className="nav-item">
                        <span>💰</span> Spending
                    </Link>
                    <Link to="/profile" className="nav-item">
                        <span>👤</span> Profile
                    </Link>
                </nav>
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Business Dashboard</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                </header>

                <main className="page-container page-fade-in">
                    <BusinessWallet />
                    <div className="stats-grid">
                        <div className="card stat-card">
                            <p className="stat-label">Total Campaigns</p>
                            <h3 className="stat-value">{campaigns.length}</h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Active Tasks</p>
                            <h3 className="stat-value">{tasks.length}</h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Total Submissions</p>
                            <h3 className="stat-value">{submissions.length}</h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Pending Review</p>
                            <h3 className="stat-value" style={{ color: 'var(--warning)' }}>
                                {submissions.filter(s => s.status === 'PENDING_BUSINESS').length}
                            </h3>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '24px', borderBottom: '1px solid var(--border)', marginBottom: '24px' }}>
                        {['campaigns', 'tasks', 'submissions'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    background: 'none', border: 'none', padding: '0 0 12px 0', cursor: 'pointer',
                                    fontSize: '14px', fontWeight: '500', textTransform: 'capitalize',
                                    color: activeTab === tab ? 'var(--text)' : 'var(--text-muted)',
                                    borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {activeTab === 'campaigns' && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '18px' }}>My Campaigns</h3>
                                <button onClick={() => setShowCampaignForm(!showCampaignForm)} className={showCampaignForm ? 'btn-secondary' : 'btn-primary'} style={{ padding: '8px 16px', fontSize: '13px' }}>
                                    {showCampaignForm ? 'Cancel' : '+ New Campaign'}
                                </button>
                            </div>

                            {showCampaignForm && (
                                <form onSubmit={handleCreateCampaign} className="card" style={{ marginBottom: '24px' }}>
                                    <h4 style={{ marginBottom: '16px', fontSize: '16px' }}>Create New Campaign</h4>
                                    <div className="form-field">
                                        <label className="form-label">Campaign Title</label>
                                        <input type="text" value={campaignData.title} onChange={(e) => setCampaignData({...campaignData, title: e.target.value})} required />
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">Description</label>
                                        <textarea value={campaignData.description} onChange={(e) => setCampaignData({...campaignData, description: e.target.value})} required style={{ minHeight: '80px' }} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div className="form-field">
                                            <label className="form-label">Total Budget ($)</label>
                                            <input type="number" value={campaignData.totalBudget} onChange={(e) => setCampaignData({...campaignData, totalBudget: e.target.value})} required />
                                            
                                            {/* NEW ESCROW WARNING */}
                                            <p style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '4px' }}>
                                                ⚠️ Creating this campaign will lock ${campaignData.totalBudget || '0'} from your available balance into Escrow.
                                            </p>
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">Number of Tasks</label>
                                            <input type="number" value={campaignData.taskCount} onChange={(e) => setCampaignData({...campaignData, taskCount: e.target.value})} required />
                                        </div>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">Campaign Type</label>
                                        <select value={campaignData.campaignType} onChange={(e) => setCampaignData({...campaignData, campaignType: e.target.value})}>
                                            <option value="PRODUCT_LAUNCH">Product Launch</option>
                                            <option value="BRAND_AWARENESS">Brand Awareness</option>
                                            <option value="SURVEY">Survey</option>
                                            <option value="REVIEW">Review</option>
                                            <option value="SOCIAL_MEDIA">Social Media</option>
                                            <option value="INSTALL">App Install</option>
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">Target Audience</label>
                                        <input type="text" value={campaignData.targetAudience} onChange={(e) => setCampaignData({...campaignData, targetAudience: e.target.value})} />
                                    </div>
                                    <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>Create Campaign</button>
                                </form>
                            )}

                            {campaigns.length === 0 ? (
                                <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                    <p>No campaigns yet. Create one above!</p>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                                    {campaigns.map(c => (
                                        <div key={c.id} className="card card-hover">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                <h4 style={{ fontSize: '16px', color: 'var(--text)' }}>{c.title}</h4>
                                                <span className={getStatusBadge(c.status)}>{c.status}</span>
                                            </div>
                                            <p style={{ fontSize: '13px', marginBottom: '16px' }}>{c.description}</p>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                                                <span>Budget: <strong style={{ color: 'var(--text)' }}>${c.totalBudget}</strong></span>
                                                <span>Tasks: <strong style={{ color: 'var(--text)' }}>{c.taskCount}</strong></span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'tasks' && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '18px' }}>My Tasks</h3>
                                <button onClick={() => setShowTaskForm(!showTaskForm)} className={showTaskForm ? 'btn-secondary' : 'btn-primary'} style={{ padding: '8px 16px', fontSize: '13px' }}>
                                    {showTaskForm ? 'Cancel' : '+ New Task'}
                                </button>
                            </div>

                            {showTaskForm && (
                                <form onSubmit={handleCreateTask} className="card" style={{ marginBottom: '24px' }}>
                                    <h4 style={{ marginBottom: '16px', fontSize: '16px' }}>Create New Task</h4>
                                    <div className="form-field">
                                        <label className="form-label">Select Campaign</label>
                                        <select value={selectedCampaignId} onChange={(e) => setSelectedCampaignId(e.target.value)} required>
                                            <option value="">Select Campaign</option>
                                            {campaigns.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">Task Title</label>
                                        <input type="text" value={taskData.title} onChange={(e) => setTaskData({...taskData, title: e.target.value})} required />
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">Description</label>
                                        <textarea value={taskData.description} onChange={(e) => setTaskData({...taskData, description: e.target.value})} required style={{ minHeight: '80px' }} />
                                    </div>
                                    
                                    <div className="form-field">
                                        <label className="form-label">Asset Link (Optional)</label>
                                        <input type="url" placeholder="https://drive.google.com/..." value={taskData.assetUrl} onChange={(e) => setTaskData({...taskData, assetUrl: e.target.value})} />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <div className="form-field">
                                            <label className="form-label">Reward ($)</label>
                                            <input type="number" value={taskData.reward} onChange={(e) => setTaskData({...taskData, reward: e.target.value})} required />
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">Deadline</label>
                                            <input type="datetime-local" value={taskData.deadline} onChange={(e) => setTaskData({...taskData, deadline: e.target.value})} required />
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label className="form-label">Minimum Worker Tier Required</label>
                                        <select value={taskData.minTier} onChange={(e) => setTaskData({...taskData, minTier: e.target.value})}>
                                            <option value="NEW">New (Anyone)</option>
                                            <option value="TRUSTED" disabled={isTierDisabled('TRUSTED')}>
                                                Trusted {isTierDisabled('TRUSTED') ? '(Requires $3+ reward)' : ''}
                                            </option>
                                            <option value="GOLD" disabled={isTierDisabled('GOLD')}>
                                                Gold {isTierDisabled('GOLD') ? '(Requires $5+ reward)' : ''}
                                            </option>
                                            <option value="PLATINUM" disabled={isTierDisabled('PLATINUM')}>
                                                Platinum {isTierDisabled('PLATINUM') ? '(Requires $10+ reward)' : ''}
                                            </option>
                                        </select>
                                        
                                        {parseFloat(taskData.reward) < 10 && (
                                            <p style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '6px' }}>
                                                ⚠️ Higher tiers require higher rewards to protect worker quality.
                                            </p>
                                        )}
                                    </div>

                                    <div className="form-field">
                                        <label className="form-label">Proof Type</label>
                                        <select value={taskData.proofType} onChange={(e) => setTaskData({...taskData, proofType: e.target.value})}>
                                            <option value="URL">URL</option>
                                            <option value="SCREENSHOT">Screenshot</option>
                                            <option value="FILE">File</option>
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">Instructions</label>
                                        <textarea value={taskData.instructions} onChange={(e) => setTaskData({...taskData, instructions: e.target.value})} style={{ minHeight: '80px' }} />
                                    </div>
                                    <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>Create Task</button>
                                </form>
                            )}

                            {tasks.length === 0 ? (
                                <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                    <p>No tasks yet. Create one above!</p>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                                    {tasks.map(t => (
                                        <div key={t.id} className="card">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                <h4 style={{ fontSize: '16px', color: 'var(--text)' }}>{t.title}</h4>
                                                <span className={getStatusBadge(t.status)}>{t.status}</span>
                                            </div>
                                            <p style={{ fontSize: '13px', marginBottom: '16px' }}>{t.description}</p>
                                            
                                            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                                {t.assetUrl && <span className="badge">📎 Asset</span>}
                                                <span className="badge">Tier: {t.minTier || 'NEW'}</span>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text-secondary)', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                                                <span>Reward: <strong style={{ color: 'var(--success)' }}>${t.reward}</strong></span>
                                                <span>{new Date(t.deadline).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'submissions' && (
                        <div>
                            <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>Worker Submissions</h3>
                            
                            {submissions.length === 0 ? (
                                <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                    <p>No submissions to review yet.</p>
                                </div>
                            ) : (
                                <div className="table-container">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Task</th>
                                                <th>Worker</th>
                                                <th>Proof</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {submissions.map(s => (
                                                <tr key={s.id}>
                                                    <td style={{ fontWeight: '500', color: 'var(--text)' }}>{s.task?.title || 'Unknown'}</td>
                                                    <td>{s.worker?.email || 'Unknown'}</td>
                                                    <td>
                                                        <a href={s.proofUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--info)', textDecoration: 'underline' }}>View Proof</a>
                                                    </td>
                                                    <td><span className={getStatusBadge(s.status)}>{s.status}</span></td>
                                                    <td style={{ fontSize: '13px' }}>{new Date(s.submittedAt).toLocaleDateString()}</td>
                                                    <td>
                                                        {s.status === 'PENDING_BUSINESS' ? (
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <button onClick={() => handleApproveSubmission(s.id)} className="btn-primary" style={{ padding: '6px 12px', fontSize: '12px' }}>Approve</button>
                                                                <button onClick={() => handleRejectSubmission(s.id)} className="btn-danger" style={{ padding: '6px 12px', fontSize: '12px' }}>Reject</button>
                                                            </div>
                                                        ) : (
                                                            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Reviewed</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}

export default BusinessDashboard;